import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import type { TransactionListParams } from '@troia-v3/types';
import { BillingService } from '../services';
import type { SubscribePlanRequest, PurchaseCreditsRequest, UpdateAutoRechargeRequest, ChangePaymentMethodRequest, CancelSubscriptionRequest, ReactivateSubscriptionRequest } from '../types';

/** Extract the backend error message from an AxiosError, falling back to error.message */
const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

/**
 * Hook para dashboard de créditos, assinatura, compra e auto-recharge
 */
export const useCredits = () => {
  const queryClient = useQueryClient();

  // Dashboard (balance, subscription, consumption)
  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['credits-dashboard'],
    queryFn: ({ signal }) => BillingService.getDashboard(signal),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Subscribe to a plan
  const subscribeMutation = useMutation({
    mutationFn: (data: SubscribePlanRequest) => BillingService.subscribe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Assinatura realizada com sucesso!');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao realizar assinatura'));
    },
  });

  // Change plan
  const changePlanMutation = useMutation({
    mutationFn: (planId: string) => BillingService.changePlan(planId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      if (result.effectiveAt === 'immediate') {
        const credits = result.additionalCredits
          ? ` +${new Intl.NumberFormat('pt-BR').format(result.additionalCredits)} creditos adicionados.`
          : '';
        const charge = result.proRataCharge
          ? ` Cobrado ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(result.proRataCharge)}.`
          : '';
        toast.success(`Plano atualizado!${charge}${credits}`);
      } else if (result.effectiveAt === 'next_cycle') {
        toast.success('Downgrade agendado para o proximo ciclo.');
      }
      // 'pending_payment' — no toast here, PlansPage handles opening PIX modal
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao alterar plano'));
    },
  });

  // Purchase additional credits
  const purchaseMutation = useMutation({
    mutationFn: (data: PurchaseCreditsRequest) => BillingService.purchaseCredits(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Créditos adquiridos com sucesso!');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao comprar créditos'));
    },
  });

  // Update auto-recharge
  const autoRechargeMutation = useMutation({
    mutationFn: (data: UpdateAutoRechargeRequest) => BillingService.updateAutoRecharge(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      toast.success('Recarga automática atualizada!');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao atualizar recarga automática'));
    },
  });

  // Change payment method
  const changePaymentMethodMutation = useMutation({
    mutationFn: (data: ChangePaymentMethodRequest) => BillingService.changePaymentMethod(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      toast.success(result.message);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao alterar forma de pagamento'));
    },
  });

  // Cancel subscription
  const cancelSubscriptionMutation = useMutation({
    mutationFn: (data: CancelSubscriptionRequest) => BillingService.cancelSubscription(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      toast.success(result.message);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao cancelar assinatura'));
    },
  });

  // Reactivate subscription
  const reactivateSubscriptionMutation = useMutation({
    mutationFn: (data: ReactivateSubscriptionRequest) => BillingService.reactivateSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Erro ao reativar assinatura'));
    },
  });

  return {
    dashboard,
    isLoading,
    error,
    refetch,
    subscribe: subscribeMutation.mutate,
    subscribeAsync: subscribeMutation.mutateAsync,
    isSubscribing: subscribeMutation.isPending,
    changePlan: changePlanMutation.mutate,
    changePlanAsync: changePlanMutation.mutateAsync,
    isChangingPlan: changePlanMutation.isPending,
    purchaseCredits: purchaseMutation.mutate,
    purchaseCreditsAsync: purchaseMutation.mutateAsync,
    isPurchasing: purchaseMutation.isPending,
    updateAutoRecharge: autoRechargeMutation.mutate,
    isUpdatingAutoRecharge: autoRechargeMutation.isPending,
    changePaymentMethod: changePaymentMethodMutation.mutate,
    changePaymentMethodAsync: changePaymentMethodMutation.mutateAsync,
    isChangingPaymentMethod: changePaymentMethodMutation.isPending,
    cancelSubscription: cancelSubscriptionMutation.mutate,
    cancelSubscriptionAsync: cancelSubscriptionMutation.mutateAsync,
    isCancellingSubscription: cancelSubscriptionMutation.isPending,
    reactivateSubscription: reactivateSubscriptionMutation.mutate,
    reactivateSubscriptionAsync: reactivateSubscriptionMutation.mutateAsync,
    isReactivating: reactivateSubscriptionMutation.isPending,
  };
};

export const useAIModelConsumption = () => {
  return useQuery({
    queryKey: ['credits', 'consumption-ai-models'],
    queryFn: ({ signal }) => BillingService.getConsumptionByAIModel(signal),
    staleTime: 5 * 60 * 1000,
  });
};

export const useChannelConsumption = () => {
  return useQuery({
    queryKey: ['credits', 'consumption-channels'],
    queryFn: ({ signal }) => BillingService.getConsumptionByChannel(signal),
    staleTime: 5 * 60 * 1000,
  });
};

export const useConsumptionByDay = () => {
  return useQuery({
    queryKey: ['credits', 'consumption-by-day'],
    queryFn: ({ signal }) => BillingService.getConsumptionByDay(signal),
    staleTime: 5 * 60 * 1000,
  });
};

export const useTransactions = (filters: TransactionListParams) => {
  return useQuery({
    queryKey: ['credits', 'transactions', filters],
    queryFn: ({ signal }) => BillingService.getTransactions(filters, signal),
    staleTime: 2 * 60 * 1000,
  });
};
